import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LayoutDashboard, ChevronRight } from "lucide-react";
import { authService } from "@/lib/api";
import { PwaInstallButton } from "@/components/PwaInstallButton";
import { openWhatsApp } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/icons/BrandIcons";

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      if (authenticated) {
        setUser(authService.getUser());
      } else {
        setUser(null);
      }
    };
    checkAuth();

    const handleStorageChange = () => checkAuth();
    window.addEventListener("storage", handleStorageChange);

    const handleFocus = () => checkAuth();
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Height of nav
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const handleDashboardClick = () => {
    if (user?.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/customer");
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: "Breeds", action: () => scrollToSection("breeds") },
    { label: "Live Tracking", action: () => scrollToSection("live-tracking") },
    { label: "Calculator", action: () => scrollToSection("calculator") },
    { label: "Qurbani 2027", action: () => scrollToSection("qurbani"), highlight: true },
    { label: "Guides", action: () => scrollToSection("guides") },
    { label: "FAQ", action: () => scrollToSection("faq") },
    { label: "Contact", action: () => scrollToSection("contact") },
  ];

  const showLandingNav = location.pathname === "/";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen
        ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border"
        : "bg-transparent"
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer z-50"
            onClick={handleLogoClick}
          >
            <span className="text-2xl sm:text-3xl filter drop-shadow-sm">🐐</span>
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              Alraheem Goat Farm
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {showLandingNav && navLinks.map((link, index) => (
              <button
                key={index}
                onClick={link.action}
                className={`text-sm font-medium transition-colors relative group ${
                  link.highlight
                    ? "text-primary font-bold"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {link.label}
                {link.highlight && (
                  <span className="absolute -top-2 -right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full" />
              </button>
            ))}
            {showLandingNav && <PwaInstallButton />}
            {isAuthenticated ? (
              <Button
                onClick={handleDashboardClick}
                size="sm"
                className="gap-2 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => openWhatsApp("hero")}
                  size="sm"
                  className="gap-2 bg-[#25D366] hover:bg-[#20BD5C] text-white shadow-lg transition-all [&_svg]:!h-4 [&_svg]:!w-4"
                >
                  <WhatsAppIcon className="h-4 w-4 text-white" />
                  WhatsApp
                </Button>
                <Button
                  onClick={() => navigate("/auth")}
                  size="sm"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  Login
                </Button>
              </>
            )}
          </div>

          {/* Mobile WhatsApp button: always visible */}
          {!isAuthenticated && (
            <button
              onClick={() => openWhatsApp("hero")}
              aria-label="WhatsApp"
              className="md:hidden ml-auto mr-2 flex items-center justify-center h-10 w-10 rounded-full bg-[#25D366] text-white active:scale-95 transition-transform shadow-md"
            >
              <WhatsAppIcon className="h-5 w-5 text-white" />
            </button>
          )}

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground z-50 active:scale-95 transition-transform"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-background z-40 md:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"
          }`}
        style={{ top: '0', paddingTop: '80px' }}
      >
        <div className="container mx-auto px-4 flex flex-col gap-2 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
          {showLandingNav && navLinks.map((link, index) => (
            <button
              key={index}
              onClick={link.action}
              className="flex items-center justify-between w-full p-4 text-lg font-medium text-foreground border-b border-border/50 active:bg-muted/50 transition-colors rounded-xl"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {link.label}
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          ))}

          <div className="mt-6 p-4 space-y-3">
            {showLandingNav && (
              <div onClick={() => setIsMobileMenuOpen(false)}>
                <PwaInstallButton />
              </div>
            )}
            {isAuthenticated ? (
              <Button
                onClick={handleDashboardClick}
                className="w-full gap-2 h-12 text-lg shadow-lg"
                size="lg"
              >
                <LayoutDashboard className="w-5 h-5" />
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    openWhatsApp("hero");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full gap-2 h-12 text-lg shadow-lg bg-[#25D366] hover:bg-[#20BD5C] text-white [&_svg]:!h-5 [&_svg]:!w-5"
                  size="lg"
                >
                  <WhatsAppIcon className="h-5 w-5 text-white" />
                  WhatsApp: Bakra Book Karein
                </Button>
                <Button
                  onClick={() => {
                    navigate("/auth");
                    setIsMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="w-full h-12 text-lg"
                  size="lg"
                >
                  Customer Login
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;

